import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Phone,
  Plus,
  Trash2,
  CheckCircle,
  AlertTriangle,
  Mic,
  Edit3,
  Play,
  Save
} from 'lucide-react'
import { apiClient } from '@/lib/api'

interface VoiceInterviewModalProps {
  open: boolean
  onClose: () => void
  candidateId: string
  positionId: string
  candidateName: string
  candidatePhone: string
  positionTitle: string
  onInterviewScheduled?: () => void
}

interface Question {
  id: string
  text: string
  maxDuration: number
}

interface VoiceInterviewData {
  candidateId: string
  positionId: string
  candidatePhone: string
  questions: Question[]
  scheduledDateTime: string
  notes: string
}

export default function VoiceInterviewModal({
  open,
  onClose,
  candidateId,
  positionId,
  candidateName,
  candidatePhone,
  positionTitle,
  onInterviewScheduled
}: VoiceInterviewModalProps) {
  const [formData, setFormData] = useState<VoiceInterviewData>({
    candidateId,
    positionId,
    candidatePhone: candidatePhone || '',
    questions: [
      {
        id: '1',
        text: `Hello, this is your automated phone screening for the ${positionTitle} role. First question: Tell me about your experience relevant to this position.`,
        maxDuration: 120
      },
      {
        id: '2',
        text: 'Thank you. Second question: What are your key skills and tools you\'re most comfortable with?',
        maxDuration: 90
      },
      {
        id: '3',
        text: 'Great. Final question: Why are you interested in this role and our company?',
        maxDuration: 90
      }
    ],
    scheduledDateTime: '',
    notes: ''
  })
  
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null)
  const [tempQuestionText, setTempQuestionText] = useState('')

  useEffect(() => {
    if (open) {
      setSubmitted(false)
      setErrors({})
      setEditingQuestionId(null)
      
      // Set default scheduled time to tomorrow at 2 PM
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(14, 0, 0, 0)
      
      setFormData(prev => ({
        ...prev,
        candidateId,
        positionId,
        candidatePhone: candidatePhone || '',
        scheduledDateTime: tomorrow.toISOString().slice(0, 16)
      }))
    }
  }, [open, candidateId, positionId, candidatePhone])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.candidatePhone) {
      newErrors.candidatePhone = 'Candidate phone number is required'
    } else if (!/^\+?[\d\s-()]+$/.test(formData.candidatePhone)) {
      newErrors.candidatePhone = 'Please enter a valid phone number'
    }
    
    if (!formData.scheduledDateTime) {
      newErrors.scheduledDateTime = 'Please select a date and time for the call'
    } else {
      const selectedDateTime = new Date(formData.scheduledDateTime)
      if (selectedDateTime < new Date()) {
        newErrors.scheduledDateTime = 'Interview cannot be scheduled in the past'
      }
    }
    
    if (formData.questions.length === 0) {
      newErrors.questions = 'At least one question is required'
    }
    
    formData.questions.forEach((question, index) => {
      if (!question.text.trim()) {
        newErrors[`question_${index}`] = 'Question text cannot be empty'
      }
    })
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setLoading(true)
    try {
      const response = await apiClient.scheduleVoiceInterview({
        candidate_id: parseInt(formData.candidateId),
        position_id: parseInt(formData.positionId),
        candidate_phone: formData.candidatePhone,
        questions: formData.questions.map(q => ({
          text: q.text,
          max_duration: q.maxDuration
        })),
        scheduled_at: new Date(formData.scheduledDateTime).toISOString(),
        notes: formData.notes
      })
      
      if (response.success) {
        setSubmitted(true)
        onInterviewScheduled?.()
        
        // Auto close after showing success
        setTimeout(() => {
          onClose()
          resetForm()
        }, 3000)
      } else {
        throw new Error(response.error || 'Failed to schedule voice interview')
      }
      
    } catch (error) {
      console.error('Failed to schedule voice interview:', error)
      setErrors({ 
        submit: error instanceof Error ? error.message : 'Failed to schedule interview. Please try again.' 
      })
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      candidateId,
      positionId,
      candidatePhone: candidatePhone || '',
      questions: [
        {
          id: '1',
          text: `Hello, this is your automated phone screening for the ${positionTitle} role. First question: Tell me about your experience relevant to this position.`,
          maxDuration: 120
        },
        {
          id: '2',
          text: 'Thank you. Second question: What are your key skills and tools you\'re most comfortable with?',
          maxDuration: 90
        },
        {
          id: '3',
          text: 'Great. Final question: Why are you interested in this role and our company?',
          maxDuration: 90
        }
      ],
      scheduledDateTime: '',
      notes: ''
    })
    setSubmitted(false)
    setErrors({})
    setEditingQuestionId(null)
  }

  const addQuestion = () => {
    const newQuestion: Question = {
      id: Date.now().toString(),
      text: 'Enter your question here...',
      maxDuration: 90
    }
    
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }))
    
    setEditingQuestionId(newQuestion.id)
    setTempQuestionText(newQuestion.text)
  }

  const removeQuestion = (questionId: string) => {
    if (formData.questions.length <= 1) return
    
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }))
  }

  const startEditingQuestion = (question: Question) => {
    setEditingQuestionId(question.id)
    setTempQuestionText(question.text)
  }

  const saveQuestionEdit = () => {
    if (!editingQuestionId) return
    
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === editingQuestionId 
          ? { ...q, text: tempQuestionText }
          : q
      )
    }))
    
    setEditingQuestionId(null)
    setTempQuestionText('')
  }

  const cancelQuestionEdit = () => {
    setEditingQuestionId(null)
    setTempQuestionText('')
  }

  const updateQuestionDuration = (questionId: string, duration: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId 
          ? { ...q, maxDuration: duration }
          : q
      )
    }))
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Schedule Voice Interview</h2>
                    <p className="text-sm text-gray-500">
                      Automated phone screening for {candidateName} - {positionTitle}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-6 text-center"
                  >
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Voice Interview Scheduled!
                    </h3>
                    <p className="text-gray-600 mb-4">
                      The automated voice interview has been scheduled successfully.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                      <p><strong>Candidate:</strong> {candidateName}</p>
                      <p><strong>Phone:</strong> {formData.candidatePhone}</p>
                      <p><strong>Scheduled:</strong> {new Date(formData.scheduledDateTime).toLocaleString()}</p>
                      <p><strong>Questions:</strong> {formData.questions.length} questions prepared</p>
                    </div>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Contact Information */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Candidate Phone Number *
                        </label>
                        <input
                          type="tel"
                          value={formData.candidatePhone}
                          onChange={(e) => setFormData(prev => ({ ...prev, candidatePhone: e.target.value }))}
                          placeholder="+1 234 567 8900"
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            errors.candidatePhone ? 'border-red-300' : ''
                          }`}
                        />
                        {errors.candidatePhone && (
                          <p className="mt-1 text-sm text-red-600">{errors.candidatePhone}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Scheduled Date & Time *
                        </label>
                        <input
                          type="datetime-local"
                          value={formData.scheduledDateTime}
                          onChange={(e) => setFormData(prev => ({ ...prev, scheduledDateTime: e.target.value }))}
                          min={new Date().toISOString().slice(0, 16)}
                          className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            errors.scheduledDateTime ? 'border-red-300' : ''
                          }`}
                        />
                        {errors.scheduledDateTime && (
                          <p className="mt-1 text-sm text-red-600">{errors.scheduledDateTime}</p>
                        )}
                      </div>
                    </div>

                    {/* Questions Section */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 flex items-center">
                            <Mic className="w-5 h-5 mr-2 text-purple-600" />
                            Interview Questions
                          </h3>
                          <p className="text-sm text-gray-500">Customize the questions that will be asked during the automated call</p>
                        </div>
                        <button
                          type="button"
                          onClick={addQuestion}
                          className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Question</span>
                        </button>
                      </div>

                      {errors.questions && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-600">{errors.questions}</p>
                        </div>
                      )}

                      <div className="space-y-4">
                        {formData.questions.map((question, index) => (
                          <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-sm font-medium text-purple-600">
                                  {index + 1}
                                </span>
                                <span className="text-sm font-medium text-gray-700">Question {index + 1}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                {editingQuestionId === question.id ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={saveQuestionEdit}
                                      className="p-1.5 text-green-600 hover:bg-green-100 rounded"
                                    >
                                      <Save className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={cancelQuestionEdit}
                                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => startEditingQuestion(question)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </button>
                                    {formData.questions.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => removeQuestion(question.id)}
                                        className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {editingQuestionId === question.id ? (
                              <textarea
                                value={tempQuestionText}
                                onChange={(e) => setTempQuestionText(e.target.value)}
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                placeholder="Enter your question..."
                              />
                            ) : (
                              <p className="text-gray-900 mb-3 bg-gray-50 p-3 rounded border">
                                {question.text}
                              </p>
                            )}

                            <div className="flex items-center space-x-4">
                              <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-600">Max Duration:</label>
                                <select
                                  value={question.maxDuration}
                                  onChange={(e) => updateQuestionDuration(question.id, parseInt(e.target.value))}
                                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                >
                                  <option value={30}>30 seconds</option>
                                  <option value={60}>1 minute</option>
                                  <option value={90}>1.5 minutes</option>
                                  <option value={120}>2 minutes</option>
                                  <option value={180}>3 minutes</option>
                                </select>
                              </div>
                            </div>

                            {errors[`question_${index}`] && (
                              <p className="mt-2 text-sm text-red-600">{errors[`question_${index}`]}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        placeholder="Any special instructions or notes about this voice interview..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                        <Play className="w-4 h-4 mr-2" />
                        How Voice Interview Works
                      </h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• System will automatically call the candidate at the scheduled time</li>
                        <li>• Questions will be asked in sequence with recording between each</li>
                        <li>• Candidate responses will be transcribed and analyzed using AI</li>
                        <li>• A comprehensive interview summary will be generated automatically</li>
                        <li>• You'll receive the results with ratings and recommendations</li>
                      </ul>
                    </div>

                    {/* Error Message */}
                    {errors.submit && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                        <p className="text-sm text-red-800">{errors.submit}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                        <Phone className="w-4 h-4" />
                        <span>{loading ? 'Scheduling...' : 'Schedule Voice Interview'}</span>
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}